import { Button, Modal, Input, message } from 'antd';
import { ThunderboltFilled, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { motion } from 'motion/react';
import React, { useState } from 'react';
import { generateAiText } from './generateAiText';
import { TONE_ENUMS } from './utils.js';
import './AiTextButton.css';

const { TextArea } = Input;

const TONES = Object.values(TONE_ENUMS);

const WRITE_SUGGESTIONS = [
    'A short blog intro',
    'A friendly follow-up email',
    'A product description',
    'A LinkedIn post',
];

const SUMMARY_SUGGESTIONS = [
    'Keep it concise',
    'Highlight key takeaways',
    'Use bullet points',
];

function AiTextButton({
    isOpen,
    setIsOpenAi,
    showAiText,
    setAiModalVisibility,
    aiModalVisibility,
    summaryContent = null,
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState({
        input: "",
        tone: "Formal",
    });

    const isSummary = Boolean(summaryContent);
    const suggestions = isSummary ? SUMMARY_SUGGESTIONS : WRITE_SUGGESTIONS;

    const applySuggestion = (text) => {
        setState((prev) => ({
            ...prev,
            input: prev.input ? `${prev.input.trim()} ${text}` : text,
        }));
    };

    const handleSubmit = async () => {
        if (!state.input && !summaryContent) {
            message.error("Please specify what you'd like to generate.");
            return;
        }

        if (!state.tone) {
            message.error("Please select a tone.");
            return;
        }

        setIsLoading(true);

        const aiQuestion = summaryContent
            ? `Write a summary covering the following points: "${summaryContent}". ${
                  state.input ? `Also include these additional parameters: ${state.input}.` : ""
              } Maximum of 60 words.`
            : `Write a paragraph covering the following points: "${state.input}" in the ${state.tone} tone. Maximum of 200 words.`;

        try {
            const response = await generateAiText(aiQuestion);
            if (response.status) {
                setIsOpenAi(false);
                showAiText(response.data);
                setAiModalVisibility(false);
                setState({ input: "", tone: "Formal" });
            } else {
                message.error(response.message || "Failed to generate text.");
            }
        } catch (error) {
            message.error("An error occurred while generating text.");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setAiModalVisibility(false);
        setState({ input: "", tone: "Formal" });
    };

    return (
        <Modal
            className="ai-gen-modal"
            onCancel={handleCancel}
            visible={aiModalVisibility}
            footer={null}
            closeIcon={<CloseOutlined />}
            width={520}
            centered
            zIndex={9999}
            destroyOnClose
        >
            {/* Hero header */}
            <div className="ai-gen__hero">
                <div className="ai-gen__hero-row">
                    <div className="ai-gen__badge">
                        <ThunderboltFilled />
                    </div>
                    <div>
                        <h3 className="ai-gen__title">Generate with AI</h3>
                        <p className="ai-gen__subtitle">
                            {isSummary
                                ? "Turn your content into a crisp, polished summary."
                                : "Describe what you need and let AI draft it for you."}
                        </p>
                    </div>
                </div>
            </div>

            {/* Body */}
            <motion.div
                className="ai-gen__body"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
            >
                <label className="ai-gen__label" htmlFor="ai-gen-input">
                    {isSummary ? "Additional parameters (optional)" : "What would you like to write?"}
                </label>
                <TextArea
                    id="ai-gen-input"
                    className="ai-gen__textarea"
                    placeholder={
                        isSummary
                            ? "e.g. focus on the main outcomes, keep it under 3 sentences…"
                            : "e.g. an engaging intro about sustainable travel for a blog…"
                    }
                    value={state.input}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    onChange={(e) => setState({ ...state, input: e.target.value })}
                    onPressEnter={(e) => {
                        if (!e.shiftKey) {
                            e.preventDefault();
                            handleSubmit();
                        }
                    }}
                    autoFocus
                />

                {/* Quick-start suggestions */}
                <div className="ai-gen__suggestions">
                    {suggestions.map((text) => (
                        <span
                            key={text}
                            className="ai-gen__chip"
                            role="button"
                            tabIndex={0}
                            onClick={() => applySuggestion(text)}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && applySuggestion(text)}
                        >
                            <PlusOutlined />
                            {text}
                        </span>
                    ))}
                </div>

                {/* Tone selector */}
                <div className="ai-gen__section">
                    <label className="ai-gen__label">Tone of voice</label>
                    <div className="ai-gen__tones">
                        {TONES.map((tone) => (
                            <span
                                key={tone}
                                className={`ai-gen__tone${state.tone === tone ? " is-active" : ""}`}
                                role="button"
                                tabIndex={0}
                                aria-pressed={state.tone === tone}
                                onClick={() => setState({ ...state, tone })}
                                onKeyDown={(e) =>
                                    (e.key === "Enter" || e.key === " ") && setState({ ...state, tone })
                                }
                            >
                                {tone}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Footer / CTA */}
                <div className="ai-gen__footer">
                    <span className="ai-gen__hint">
                        Press <strong>Enter</strong> to generate · <strong>Shift + Enter</strong> for a new line
                    </span>
                    <Button
                        className="ai-gen__generate"
                        loading={isLoading}
                        htmlType="submit"
                        icon={!isLoading && <ThunderboltFilled />}
                        onClick={handleSubmit}
                    >
                        {isLoading ? "Generating…" : "Generate"}
                    </Button>
                </div>
            </motion.div>
        </Modal>
    );
}

export default AiTextButton;
